import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ShoppingCart } from 'lucide-react'
import type { Product, ProductAccessory, ProductAccessoryTarget } from '../../types'
import { useCart } from '../../context/CartContext'
import { formatPrice } from '../../lib/utils'

type ResolvedAccessory = ProductAccessory & { resolved: ProductAccessoryTarget }

/**
 * "Dit heeft u ook nodig" block (T4.3 §6.1.1 / verbeterplan §7.1). Solves the
 * classic B&R instap-fout: an X20 module needs its bus module + terminal block,
 * and new buyers order only the electronics module. Required accessories are
 * pre-checked, recommended ones unchecked; one button adds all checked rows.
 *
 * Only accessories that resolve to an active, visible product are shown (the API
 * already filters). No such accessories → renders nothing (G13). Interactive, so
 * it hydrates on the client; SSR renders the default-checked state.
 */
export default function AccessoryBundle({ product }: { product: Product }) {
  const { t } = useTranslation()
  const { addItem } = useCart()

  const items = useMemo<ResolvedAccessory[]>(
    () => (product.accessories ?? []).filter((a): a is ResolvedAccessory => a.resolved != null),
    [product.accessories],
  )

  const [checked, setChecked] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(items.map((a) => [a.mpn, a.relation_type === 'required'])),
  )

  if (items.length === 0) return null

  const selected = items.filter((a) => checked[a.mpn])

  function addAll() {
    for (const a of selected) {
      const r = a.resolved
      addItem({
        normalizedMpn: r.normalized_mpn,
        slug: r.slug,
        displayTitle: r.display_title,
        effectivePrice: r.effective_price,
        effectivePriceIncVat: r.effective_price_inc_vat,
        primaryImageUrl: r.primary_image_url,
        moq: 1,
        quantity: a.quantity,
      })
    }
    toast.success(t('shop.bundle.added', { count: selected.length }))
  }

  return (
    <section className="mt-8 rounded-xl border border-gcg-border bg-white p-6" aria-labelledby="bundle-heading">
      <h2 id="bundle-heading" className="text-lg font-bold text-gcg-text">{t('shop.bundle.heading')}</h2>
      <p className="text-sm text-neutral-600 mt-1 mb-4">{t('shop.bundle.subtitle')}</p>

      <ul className="divide-y divide-neutral-100">
        {items.map((a) => {
          const r = a.resolved
          const priceNum = r.effective_price ? parseFloat(r.effective_price) : null
          return (
            <li key={a.mpn} className="flex items-center gap-3 py-3">
              <input
                type="checkbox"
                checked={!!checked[a.mpn]}
                onChange={() => setChecked((c) => ({ ...c, [a.mpn]: !c[a.mpn] }))}
                className="h-4 w-4 accent-primary shrink-0"
                aria-label={r.display_title}
              />
              <img
                src={r.primary_image_url ?? '/placeholder-product.svg'}
                alt=""
                className="h-12 w-12 object-contain shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-neutral-500">{r.normalized_mpn}</span>
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                      a.relation_type === 'required'
                        ? 'bg-primary/10 text-primary'
                        : 'bg-neutral-100 text-neutral-500'
                    }`}
                  >
                    {t(a.relation_type === 'required' ? 'shop.bundle.required' : 'shop.bundle.recommended')}
                  </span>
                </div>
                <div className="text-sm text-neutral-900 truncate">{r.display_title}</div>
              </div>
              <div className="text-right shrink-0">
                {a.quantity > 1 && <div className="text-xs text-neutral-500">{a.quantity}×</div>}
                <div className="text-sm font-semibold text-gcg-text">
                  {priceNum != null ? formatPrice(priceNum) : t('shop.bundle.onRequest')}
                </div>
              </div>
            </li>
          )
        })}
      </ul>

      <button
        type="button"
        onClick={addAll}
        disabled={selected.length === 0}
        className="mt-4 inline-flex items-center gap-2 px-6 py-3 bg-primary text-white text-sm font-semibold rounded-md hover:bg-primary-hover transition-colors disabled:opacity-50"
      >
        <ShoppingCart className="h-4 w-4" />
        {t('shop.bundle.addAll', { count: selected.length })}
      </button>
    </section>
  )
}
